import { useCallback, useContext, useState } from "react";
import { loginService, logoutService } from "services/loginService";
import Context from "contextAPI/UserContext";
import { useHistory } from "react-router-dom";
import {
  ACCESS_TOKEN_NAME,
  USER_DETAIL,
  DOMAIN,
  TYPE,
  ADD_REGISTER,
  EDIT_REGISTER,
  VIEW_REGISTER,
  UPLOAD_FILES,
  CREATE_EXPEDIENT,
  SET_PATH_EXPEDIENT,
  DELETE_PATH_EXPEDIENT,
  VIEW_FILES,
  EDIT_FILE_DATA,
  MOVE_FILE,
  MOVE_FILE_TO_TRASH,
  SIGN_ELECTRONICALLY,
  NOTIFICATIONS,
  INTEGRATIONS,
  ROLES_PERMISSIONS,
  CANCEL_RECORD,
  ADD_WORKFLOW,
  VIEW_WORKFLOW,
  DESIGNER_FORMS,
  DESIGNER_FORMS_PERMISSIONS,
  PASSWORD_EXPIRATION_DAYS,
  WORKFLOW_FORMS_PERMISSIONS,
  WORKFLOW_ALL_FORMS,
  FIRST_EXECUTION_BOOLEAN,
} from "constants/securityConst";
import { setItemLocalStorage } from "utils/localStorage";
import { enumsPermissions } from "utils/enums";
import {
  ShowAlertLoginError,
  ShowAlertLoginErrorDecripted,
  ShowUserAlertMaxUsers,
  ShowUserAlertWithoutRoles,
} from "utils/alerts";
import { isNullOrUndefined } from "utils/validations";
import fernet from "fernet";
import crypto from "crypto-browserify";
import swal from "sweetalert";
import i18n from "locales/i18n";

/**
 * Decrypts login data using AES-256-GCM and Fernet encryption.
 * @param {string} encryptedData - The encrypted login data to be decrypted.
 * @param {Buffer} encryptedFernetKey - The encrypted Fernet key used for decryption.
 * @param {Buffer} salt - The salt used in the key derivation process.
 * @param {Buffer} nonce - The nonce used for AES-GCM decryption.
 * @param {Buffer} derivedKey - The derived key used for AES-GCM decryption.
 * @returns {Object} The decrypted login data as a JSON object.
 * @throws Will throw an error if decryption fails.
 */
const decryptLoginData = (encryptedData, encryptedFernetKey, salt, nonce, derivedKey) => {
  try {
    const aesGcm = crypto.createDecipheriv("aes-256-gcm", derivedKey, nonce);
    aesGcm.setAuthTag(encryptedFernetKey.subarray(encryptedFernetKey.length - 16));
    const fernetKey = Buffer.concat([
      aesGcm.update(encryptedFernetKey.subarray(0, encryptedFernetKey.length - 16)),
      aesGcm.final(),
    ]);

    const secret = new fernet.Secret(fernetKey.toString("utf-8"));
    const token = new fernet.Token({
      secret: secret,
      token: encryptedData,
      ttl: 0,
    });
    const decoded = token.decode();

    return JSON.parse(decoded);
  } catch (error) {
    ShowAlertLoginErrorDecripted();
    throw error;
  }
};

/**
 * Decrypts an encrypted Fernet key using AES-256-GCM.
 * @param {Uint8Array} encryptedFernetKey - The encrypted Fernet key with authentication tag appended.
 * @param {Buffer} nonce - The nonce used for encryption.
 * @param {Buffer} derivedKey - The derived key for decryption.
 * @returns {string} The decrypted Fernet key as a UTF-8 string.
 * @throws Will throw an error if decryption fails or if the encrypted data is too short.
 */
const decryptLoginFernetKey = (encryptedFernetKey, nonce, derivedKey) => {
  try {
    if (encryptedFernetKey.length < 16) {
      throw new Error("Encrypted data too short.");
    }

    const tag = encryptedFernetKey.subarray(encryptedFernetKey.length - 16);
    const ciphertext = encryptedFernetKey.subarray(0, encryptedFernetKey.length - 16);

    const decipher = crypto.createDecipheriv("aes-256-gcm", derivedKey, nonce);
    decipher.setAuthTag(tag);

    const decrypted = Buffer.concat([decipher.update(ciphertext), decipher.final()]);
    return decrypted.toString("utf-8");
  } catch (error) {
    ShowAlertLoginErrorDecripted();
    throw error;
  }
};

/**
 * Restores the original key from a scrambled key using a given pattern.
 * @param {Buffer} scrambledKey - The scrambled key as a buffer.
 * @param {Array<number>} pattern - An array representing the scrambling pattern,
 * where each index corresponds to the position in the scrambled key and its value
 * is the position in the original key.
 * @returns {Buffer} The original key restored from the scrambled key.
 */
const restoreKey = (scrambledKey, pattern) => {
  const originalKey = Buffer.alloc(scrambledKey.length);
  pattern.forEach((scrambledIndex, originalIndex) => {
    originalKey[scrambledIndex] = scrambledKey[originalIndex];
  });
  return originalKey;
};

export default function useLogin() {
  let history = useHistory();
  const { jwt, setJwt, setUser, state, setState } = useContext(Context);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Asynchronous function that performs user login with provided credentials and manages related state and local storage.
   * @function
   * @name login
   * @param {string} userName - The username entered by the user.
   * @param {string} password - The password entered by the user.
   * @param {function} setIsLoading - State setter function to update loading state.
   * @param {function} setJwt - State setter function to update the JWT token.
   * @param {function} setUser - State setter function to update user details.
   * @param {object} setState - State setter function to update component state.
   * @param {object} i18n - Object providing internationalization functionality.
   * @returns {void}
   */
  const login = useCallback(
    (userName, password) => {
      setIsLoading(true);
      loginService(userName, password)
        .then((response) => {
          let errorsInLogin = false;
          const { ed, efk, s, n, dk } = response;
          const decodedSalt = Buffer.from(s, "base64");
          const decodedNonce = Buffer.from(n, "base64");
          const decodedEncryptedFernetKey = Buffer.from(efk, "base64");
          const decodedDerivedKey = Buffer.from(dk, "base64");
          const scramblePattern = response.p;

          const restoredKey = restoreKey(decodedDerivedKey, scramblePattern);
          const decodedToken = decryptLoginData(
            ed,
            decodedEncryptedFernetKey,
            decodedSalt,
            decodedNonce,
            restoredKey
          );

          const fernetKey = decryptLoginFernetKey(
            decodedEncryptedFernetKey,
            decodedNonce,
            restoredKey
          );

          if (
            isNullOrUndefined(decodedToken.access) === false &&
            isNullOrUndefined(fernetKey) === false
          ) {
            window.localStorage.setItem("KEY_35", fernetKey);
            const {
              user,
              admin_forms: { all_forms, forms_uuid },
              admin_workflows: { all_workflows, workflows_uuid },
              form_permissions,
              roles_permissions,
              integrations,
            } = decodedToken;

            const userPermissions = {
              [ACCESS_TOKEN_NAME]: decodedToken.access,
              [DOMAIN]: user.domain,
              [TYPE]: user.user_type,
              [DESIGNER_FORMS]: all_forms,
              [WORKFLOW_ALL_FORMS]: all_workflows,
              [USER_DETAIL]: user,
              [PASSWORD_EXPIRATION_DAYS]: user.password_expiration_days,
              [ROLES_PERMISSIONS]: roles_permissions,
              [INTEGRATIONS]: integrations,
              [DESIGNER_FORMS_PERMISSIONS]: forms_uuid,
              [WORKFLOW_FORMS_PERMISSIONS]: workflows_uuid,
              [ADD_REGISTER]: form_permissions[enumsPermissions.ADD_RECORD],
              [VIEW_REGISTER]: form_permissions[enumsPermissions.VIEW_RECORD],
              [EDIT_REGISTER]: form_permissions[enumsPermissions.EDIT_RECORDS],
              [CANCEL_RECORD]: form_permissions[enumsPermissions.CANCEL_RECORD],
              [NOTIFICATIONS]: form_permissions[enumsPermissions.ADD_NOTIFICATION],
              [VIEW_FILES]: form_permissions[enumsPermissions.VIEW_FILE],
              [UPLOAD_FILES]: form_permissions[enumsPermissions.UPLOAD_FILE],
              [VIEW_WORKFLOW]: form_permissions[enumsPermissions.VIEW_PROCESS],
              [ADD_WORKFLOW]: form_permissions[enumsPermissions.ADD_PROCESS],
              [EDIT_FILE_DATA]: form_permissions[enumsPermissions.EDIT_FILE_DATA],
              [CREATE_EXPEDIENT]: form_permissions[enumsPermissions.CREATE_EXPEDIENT],
              [MOVE_FILE_TO_TRASH]: form_permissions[enumsPermissions.MOVE_FILE_TO_TRASH],
              [MOVE_FILE]: form_permissions[enumsPermissions.MOVE_FILE],
              [SIGN_ELECTRONICALLY]: form_permissions[enumsPermissions.SIGN_ELECTRONICALLY],
              [SET_PATH_EXPEDIENT]: form_permissions[enumsPermissions.SET_EXPEDIENT],
              [DELETE_PATH_EXPEDIENT]: form_permissions[enumsPermissions.REMOVE_EXPEDIENT],
              [FIRST_EXECUTION_BOOLEAN]: true,
            };

            Object.entries(userPermissions).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                setItemLocalStorage(key, value);
              }
            });

            setJwt(decodedToken.access);
            setUser(decodedToken.user);

            if (decodedToken.password_expiration_days === 0) {
              history.push("/renew-password");
            }
          } else {
            errorsInLogin = true;
            setState({ loading: false, error: errorsInLogin, countError: 0 });
          }
        })
        .catch((errorsInLogin) => {
          window.localStorage.clear();
          setState({
            loading: false,
            error: true,
            countError: state.countError,
          });

          if (
            isNullOrUndefined(errorsInLogin.response) === false &&
            errorsInLogin.response.data.code === "10024"
          ) {
            ShowUserAlertWithoutRoles();
          } else if (
            isNullOrUndefined(errorsInLogin.response) === false &&
            errorsInLogin.response.data.code === "500080"
          ) {
            ShowUserAlertMaxUsers();
          } else {
            ShowAlertLoginError();
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [setJwt, setIsLoading, setState]
  );

  /**
   * Removes various items from local storage related to user session and preferences,
   * clears JWT token and user data, and redirects to the home page ("/").
   * @param {function} setJwt - State setter function to clear the JWT token.
   * @returns {void}
   */
  const logout = useCallback(() => {
    logoutService(jwt)
      .then((response) => {
        if (response.status === 200) {
          window.localStorage.clear();
          setJwt(null);
          setUser({});
          history.push("/");
        } else {
          ShowAlertLoginError();
        }
      })
      .catch((error) => {
        if (error.response.data.code === "500079") {
          swal({
            title: i18n.t("modal.DoneError.header"),
            text: i18n.t("showNotification401"),
            icon: "error",
            button: i18n.t("modal.Done.footerButton"),
            dangerMode: true,
          }).then(function () {
            window.localStorage.clear();
            setJwt(null);
            setUser({});
            history.push("/");
          });
        }
      });
  }, [setJwt]);

  return {
    jwt,
    isLogged: Boolean(jwt),
    isLoginLoading: state.loading,
    hasLoginError: Boolean(state.error),
    hasLoginCount: state.countError,
    isLoading,
    login,
    state,
    logout,
  };
}
